import React, { useState, useEffect, useMemo } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import "./App.css";
import Captcha from "./Captcha";
import About from "./About";
import Contact from "./Contact";
import { generateBannerContent } from "./utils";

type Lang = 'en' | 'es' | 'ja';

const detectSystemLang = (): Lang => {
  try {
    const tag = (navigator?.language || 'en').toLowerCase();
    if (tag.startsWith('es')) return 'es';
    if (tag.startsWith('ja')) return 'ja';
  } catch {}
  return 'en';
};

const LANG_LABELS: Record<Lang, string> = {
  en: 'EN',
  es: 'ESP',
  ja: '日本語',
};

const FooterWithBanner: React.FC<{ language: Lang }> = ({ language }) => {
  const location = useLocation();
  // Rebuild the (expensive) banner only when the language changes.
  const banner = useMemo(() => generateBannerContent(language), [language]);
  if (location.pathname === '/contact') return null;

  return (
    <footer>
      <div className="banner">
        {/* Two identical tracks so the marquee can loop seamlessly (animate -50%). */}
        <div className="banner-content">
          <span className="banner-track">{banner}</span>
          <span className="banner-track" aria-hidden="true">{banner}</span>
        </div>
      </div>
    </footer>
  );
};

const App: React.FC = () => {
  const [verified, setVerified] = useState(false);

  const [language, setLanguage] = useState<Lang>(() => {
    const saved = localStorage.getItem('uiLang') as Lang | null;
    return saved ?? detectSystemLang();
  });

  const [userOverrodeLang, setUserOverrodeLang] = useState<boolean>(() => {
    return localStorage.getItem('uiLang') != null;
  });

  const [fadeOut, setFadeOut] = useState(false);

  useEffect(() => {
    const hasVisited = sessionStorage.getItem("visited");
    if (hasVisited) setVerified(true);
  }, []);

  // Keep the document language in sync for accessibility/SEO.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    const handler = () => {
      if (!userOverrodeLang) {
        setLanguage(detectSystemLang());
      }
    };
    window.addEventListener('languagechange', handler);
    return () => window.removeEventListener('languagechange', handler);
  }, [userOverrodeLang]);

  const handleSuccess = () => {
    setFadeOut(true);
    setTimeout(() => {
      sessionStorage.setItem("visited", "true");
      setVerified(true);
    }, 300);
  };

  const handleLanguageChange = (newLanguage: Lang) => {
    setLanguage(newLanguage);
    setUserOverrodeLang(true);
    localStorage.setItem('uiLang', newLanguage);
  };

  return (
    <Router>
      <div className="App">
        {!verified && (
          <div className={`captcha-container ${fadeOut ? 'fade-out' : ''}`}>
            <Captcha onSolved={handleSuccess} />
          </div>
        )}

        <div className={`about-container ${verified ? 'fade-in' : 'hidden'}`}>
          <header>
            <div className="language-switch">
              {(['en', 'es', 'ja'] as Lang[]).map((lng) => (
                <button
                  key={lng}
                  className={language === lng ? 'selected' : ''}
                  onClick={() => handleLanguageChange(lng)}
                  aria-pressed={language === lng}
                >
                  {LANG_LABELS[lng]}
                </button>
              ))}
            </div>
          </header>

          <Routes>
            <Route path="/" element={<About language={language} />} />
            <Route path="/contact" element={<Contact language={language} />} />
          </Routes>

          <FooterWithBanner language={language} />
        </div>
      </div>
    </Router>
  );
};

export default App;
